import { useId, useState, type FormEvent } from "react";
import type { MoneyAccount } from "@/api/cash";
import {
  BILLING_CYCLES,
  EXPENSE_METHODS,
  EXPENSE_STATUSES,
  SPEND_PURPOSES,
  type BillingCycle,
  type Category,
  type ExpenseInput,
  type ExpenseMethod,
  type ExpenseStatus,
  type SpendPurpose,
} from "@/api/finance";
import { DriveLinkField, type DriveUploader } from "@/components/forms/DriveLinkField";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { formatInr } from "@/lib/format";
import { useFormValues, useSubmitState } from "@/lib/use-form";

/** A payout of one invoice's split: recorded in the invoice's currency, against it. */
export type Payout = { invoiceId: string; invoiceNo: string | null; currency: string };

type ExpenseFormProps = {
  /** The buckets it can be charged to; a payout only to the invoice's own. */
  buckets: { id: string; name: string }[];
  /** What kind of cost it is; choosing one picks its default bucket. Not for a payout. */
  categories?: Category[];
  payout?: Payout;
  /** The money accounts it may be paid from; the select appears only when given. */
  accounts?: MoneyAccount[];
  /** Attach the bill or proof to the company Drive; without it the links are typed only. */
  drive?: DriveUploader;
  initialBucketId?: string;
  initialPayee?: string;
  initialAmount?: string;
  /** Rupees per unit to start from, e.g. the rate the invoice achieved for a payout. */
  initialFxRate?: string;
  initialBillingCycle?: BillingCycle;
  submitLabel: string;
  onSubmit: (input: ExpenseInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/** amount × rate in rupees, rounded to paise; null until both are numbers above zero. */
function rupees(amount: string, rate: string): string | null {
  const value = Math.round(Number(amount) * Number(rate) * 100) / 100;
  return Number.isFinite(value) && value > 0 ? value.toFixed(2) : null;
}

// The sections a small company deducts under: salary, contractors, commission, rent,
// professional fees, payments abroad.
const TDS_SECTIONS: { value: string; label: string }[] = [
  { value: "192", label: "192 - Salary" },
  { value: "194C", label: "194C - Contractor" },
  { value: "194H", label: "194H - Commission" },
  { value: "194I", label: "194I - Rent" },
  { value: "194J", label: "194J - Professional / technical fees" },
  { value: "195", label: "195 - Payment to a non-resident" },
];

export function ExpenseForm({
  buckets,
  categories,
  payout,
  accounts,
  drive,
  initialBucketId = "",
  initialPayee = "",
  initialAmount = "",
  initialFxRate = "",
  initialBillingCycle,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
}: ExpenseFormProps) {
  const id = useId();
  const initialCurrency = payout?.currency ?? "INR";
  const openAccounts = accounts?.filter((a) => !a.archived_at);
  const openCategories = payout ? undefined : categories?.filter((c) => !c.archived);
  // The default-out account may be the dollar bank; a rupee expense cannot sit there.
  const defaultAccount = openAccounts?.find(
    (a) => a.is_default_out && (a.currency === initialCurrency || a.currency === "INR"),
  );
  const { values, set } = useFormValues({
    spent_on: todayIso(),
    category_id: "",
    bucket_id: initialBucketId,
    purpose: "" as SpendPurpose | "",
    payee: initialPayee,
    description: payout ? `Payout - ${payout.invoiceNo ?? "draft"}` : "",
    status: "paid" as ExpenseStatus,
    method: "" as ExpenseMethod,
    currency: initialCurrency,
    amount: initialAmount,
    gst_included: "",
    tds_section: "",
    tds_amount: "",
    fx_rate: initialFxRate,
    paid_from_id: defaultAccount?.id ?? "",
    billing_cycle: (initialBillingCycle ?? "") as BillingCycle | "",
    period_from: "",
    period_to: "",
    bill_url: "",
    proof_url: "",
    reference: "",
    notes: "",
  });
  const { error, errors, saving, run } = useSubmitState();
  const [uploads, setUploads] = useState(0);
  const countUpload = (busy: boolean): void => setUploads((n) => n + (busy ? 1 : -1));
  const currency = values.currency.trim().toUpperCase() || "INR";
  const foreign = currency !== "INR";
  const inr = foreign ? rupees(values.amount, values.fx_rate) : null;
  const unpaid = values.status === "unpaid";
  const reimburse = values.status === "to_reimburse";
  // A bill to reimburse is owed to whoever paid it: one of the people.
  const payers = reimburse ? openAccounts?.filter((a) => a.kind === "person") : openAccounts;
  const recurring = values.billing_cycle !== "";
  // Names an attached file in Drive: "<date> <payee> - <file name>".
  const fileLabel = values.payee.trim() || values.description.trim();
  const paidFrom = openAccounts?.find((a) => a.id === values.paid_from_id);
  const cannotPlace =
    paidFrom !== undefined && paidFrom.currency !== currency && paidFrom.currency !== "INR";

  function chooseCategory(categoryId: string): void {
    set("category_id", categoryId);
    const chosen = openCategories?.find((c) => c.id === categoryId);
    if (chosen) set("bucket_id", chosen.default_bucket_id);
  }

  function chooseStatus(status: string): void {
    set("status", status as ExpenseStatus);
    if (status === "unpaid") set("paid_from_id", "");
    if (status === "to_reimburse" && paidFrom?.kind !== "person") set("paid_from_id", "");
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        spent_on: values.spent_on,
        bucket_id: values.bucket_id || null,
        category_id: payout ? null : values.category_id || null,
        invoice_id: payout?.invoiceId ?? null,
        paid_from_id: unpaid ? null : values.paid_from_id || null,
        payee: values.payee.trim(),
        description: values.description.trim(),
        purpose: payout ? null : values.purpose || null,
        status: payout ? "paid" : values.status,
        method: values.method,
        currency,
        amount: values.amount.trim(),
        gst_included: values.gst_included.trim() || "0",
        tds_section: values.tds_section,
        tds_amount: values.tds_amount.trim() || "0",
        fx_rate: foreign ? values.fx_rate.trim() || null : null,
        billing_cycle: payout ? null : values.billing_cycle || null,
        period_from: recurring && !payout ? values.period_from || null : null,
        period_to: recurring && !payout ? values.period_to || null : null,
        bill_url: values.bill_url.trim(),
        proof_url: values.proof_url.trim(),
        reference: values.reference.trim(),
        notes: values.notes.trim(),
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-date`}
          label={unpaid ? "Bill date" : "Paid on"}
          type="date"
          value={values.spent_on}
          onChange={(value) => set("spent_on", value)}
          error={errors.spent_on}
        />
        {openCategories && (
          <SelectField
            id={`${id}-category`}
            label="Category"
            value={values.category_id}
            onChange={chooseCategory}
            options={openCategories.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Choose a category"
            error={errors.category_id}
            hint="What kind of cost; it picks the bucket"
          />
        )}
        <SelectField
          id={`${id}-bucket`}
          label="Bucket"
          value={values.bucket_id}
          onChange={(value) => set("bucket_id", value)}
          options={buckets.map((bucket) => ({ value: bucket.id, label: bucket.name }))}
          placeholder="Choose a bucket"
          error={errors.bucket_id}
          hint="Which part of the income this comes out of"
        />
        {!payout && (
          <SelectField
            id={`${id}-purpose`}
            label="Purpose"
            value={values.purpose}
            onChange={(value) => set("purpose", value as SpendPurpose | "")}
            options={SPEND_PURPOSES}
            placeholder="Not said"
            error={errors.purpose}
            hint="Why the money was spent"
          />
        )}
        <TextField
          id={`${id}-payee`}
          label="Paid to"
          value={values.payee}
          onChange={(value) => set("payee", value)}
          error={errors.payee}
        />
        <TextField
          id={`${id}-description`}
          label="What for"
          value={values.description}
          onChange={(value) => set("description", value)}
          error={errors.description}
          className={payout ? "md:col-span-2" : "md:col-span-3"}
          hint={recurring ? "Keep the same name every time, so renewals are tracked" : undefined}
        />
        {!payout && (
          <SelectField
            id={`${id}-status`}
            label="Status"
            value={values.status}
            onChange={chooseStatus}
            options={EXPENSE_STATUSES}
            error={errors.status}
          />
        )}
        <SelectField
          id={`${id}-method`}
          label="Method"
          value={values.method}
          onChange={(value) => set("method", value as ExpenseMethod)}
          options={EXPENSE_METHODS}
          placeholder="Not said"
          error={errors.method}
        />
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={values.currency}
          onChange={(value) => set("currency", value.toUpperCase())}
          error={errors.currency}
          maxLength={3}
          autoCapitalize="characters"
          disabled={payout !== undefined}
          hint={payout ? "A payout is in the invoice's currency" : "INR unless paid in another"}
        />
        <TextField
          id={`${id}-amount`}
          label={`Amount (${currency})`}
          inputMode="decimal"
          value={values.amount}
          onChange={(value) => set("amount", value)}
          error={errors.amount}
          hint="Including GST"
        />
        <TextField
          id={`${id}-gst`}
          label="GST included"
          inputMode="decimal"
          value={values.gst_included}
          onChange={(value) => set("gst_included", value)}
          error={errors.gst_included}
          hint="The GST inside the amount, if any"
        />
        {!payout && (
          <SelectField
            id={`${id}-tds-section`}
            label="TDS section"
            value={values.tds_section}
            onChange={(value) => set("tds_section", value)}
            options={TDS_SECTIONS}
            placeholder="No TDS"
            error={errors.tds_section}
            hint="When the company must deduct tax from this bill"
          />
        )}
        {!payout && values.tds_section !== "" && (
          <TextField
            id={`${id}-tds-amount`}
            label="TDS amount"
            inputMode="decimal"
            value={values.tds_amount}
            onChange={(value) => set("tds_amount", value)}
            error={errors.tds_amount}
            hint={`In ${currency}; the payee gets the amount less this`}
          />
        )}
        {foreign && (
          <TextField
            id={`${id}-rate`}
            label="Rupees per unit"
            inputMode="decimal"
            value={values.fx_rate}
            onChange={(value) => set("fx_rate", value)}
            error={errors.fx_rate}
            hint={inr ? `= ${formatInr(inr)}` : `INR per 1 ${currency}, the rate you paid at`}
          />
        )}
        {payers && !unpaid && (
          <SelectField
            id={`${id}-paid-from`}
            label={reimburse ? "Paid by" : "Paid from"}
            value={values.paid_from_id}
            onChange={(value) => set("paid_from_id", value)}
            options={payers.map((a) => ({ value: a.id, label: `${a.name} (${a.currency})` }))}
            placeholder={reimburse ? "Choose who paid" : "Not specified"}
            error={errors.paid_from_id}
            hint={
              cannotPlace && paidFrom
                ? `${paidFrom.name} cannot hold ${currency}: the expense will show as not converted`
                : reimburse
                  ? "Who paid it: a person, or a company, added as a person on Money → Accounts"
                  : "Which money account it left"
            }
          />
        )}
        <TextField
          id={`${id}-reference`}
          label="Reference"
          value={values.reference}
          onChange={(value) => set("reference", value)}
          error={errors.reference}
          hint="UTR, cheque or vendor invoice number, optional"
        />
        {!payout && (
          <SelectField
            id={`${id}-cycle`}
            label="Billing cycle"
            value={values.billing_cycle}
            onChange={(value) => set("billing_cycle", value as BillingCycle | "")}
            options={BILLING_CYCLES}
            placeholder="Not a recurring bill"
            error={errors.billing_cycle}
            hint="For subscriptions and renewals"
          />
        )}
        {!payout && recurring && (
          <>
            <TextField
              id={`${id}-from`}
              label="Period from"
              type="date"
              value={values.period_from}
              onChange={(value) => set("period_from", value)}
              error={errors.period_from}
            />
            <TextField
              id={`${id}-to`}
              label="Period to"
              type="date"
              value={values.period_to}
              onChange={(value) => set("period_to", value)}
              error={errors.period_to}
              hint="The next renewal is the day after"
            />
          </>
        )}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <DriveLinkField
          id={`${id}-bill`}
          label="Bill link"
          kind="bill"
          on={values.spent_on}
          fileLabel={fileLabel}
          value={values.bill_url}
          onChange={(value) => set("bill_url", value)}
          error={errors.bill_url}
          hint="Optional: a link to the bill in the company Drive (https)"
          drive={drive}
          onBusy={countUpload}
        />
        <DriveLinkField
          id={`${id}-proof`}
          label="Proof link"
          kind="proof"
          on={values.spent_on}
          fileLabel={fileLabel}
          value={values.proof_url}
          onChange={(value) => set("proof_url", value)}
          error={errors.proof_url}
          hint="Optional: a link to the payment proof in the company Drive (https)"
          drive={drive}
          onBusy={countUpload}
        />
      </div>
      <TextAreaField
        id={`${id}-notes`}
        label="Notes"
        value={values.notes}
        onChange={(value) => set("notes", value)}
        error={errors.notes}
        rows={2}
      />
      <FormActions
        saving={saving}
        submitLabel={submitLabel}
        onCancel={onCancel}
        disabled={uploads > 0}
      />
    </form>
  );
}
